import React, { useEffect, useState } from 'react';
import Skeleton from '@mui/material/Skeleton';

import {
  AddressWrapper,
  DataRowContainer,
  FieldsContainer,
  MasterdataWrapper,
  Section,
  SectionContainer,
  SectionTitle,
} from './styles';
import { TranslationKeys } from '../../i18n/locales/defaultResources';
import { useBpStore } from '../../stores/bpStore';
import { useContextStore } from '../../stores/storeRoot';
import { useI18n } from '../../hooks/useI18n';
import PageHeader from '../../presentation/layout/PageHeader/PageHeader';
import { MainContainer } from '../../styles/styles';
import DataRow from '../../presentation/components/Inputs/DataRow/DataRow';
import { cropConnectApiClient } from '../../services/cropConnectApiClient';

const sectionFieldMap = {
  general: [
    'title',
    'name',
    'id',
    'office',
    'euFarmNumber',
    'personalIdentityCard',
    'personalIdentityCardValidFrom',
    'socialSecurityNumber',
  ],
  address: [
    'streetName',
    'houseNo',
    'street_5',
    'postalCode',
    'city',
    'district',
  ],
  communication: ['telephoneNumbers', 'fax', 'emails'],
  bankAndVat: [
    'bankName',
    'bankAccountNumber',
    'bankCode',
    'iban',
    'taxNumber',
    'vatNumber',
    'vatOrRR',
  ],
  transport: [
    'bezungspunkt',
    'bzpinKm',
    'frachtsatzUeberruebenFuerBZP',
    'frachtsatzProdukteFuerBZP',
  ],
};

function convertIntoGroupMasterData(flatData) {
  const grouped = {};

  for (const section in sectionFieldMap) {
    const sectionFields = sectionFieldMap[section];
    const sectionData = {};

    for (const field of sectionFields) {
      if (Object.prototype.hasOwnProperty.call(flatData, field)) {
        sectionData[field] = flatData[field];
      }
    }

    if (Object.keys(sectionData).length > 0) {
      grouped[section] = sectionData;
    }
  }

  return grouped;
}

const MasterdataSkeleton = () => (
  <MainContainer>
    <PageHeader
      title={
        <Skeleton variant="text" width={200} height={32} animation="wave" />
      }
      chip={
        <Skeleton
          variant="rectangular"
          width={80}
          height={32}
          animation="wave"
          style={{ marginLeft: '1rem' }}
        />
      }
    />
    <Section>
      <SectionTitle>
        <Skeleton variant="text" width={150} height={24} animation="wave" />
      </SectionTitle>
      <MasterdataWrapper>
        <FieldsContainer>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={56}
            animation="wave"
            style={{ borderRadius: '8px' }}
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={56}
            animation="wave"
            style={{ borderRadius: '8px' }}
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={56}
            animation="wave"
            style={{ borderRadius: '8px' }}
          />
        </FieldsContainer>
      </MasterdataWrapper>
    </Section>
    <Section>
      <SectionTitle>
        <Skeleton variant="text" width={150} height={24} animation="wave" />
      </SectionTitle>
      <AddressWrapper>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={56}
          animation="wave"
          style={{ borderRadius: '8px' }}
        />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={56}
          animation="wave"
          style={{ borderRadius: '8px' }}
        />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={56}
          animation="wave"
          style={{ borderRadius: '8px' }}
        />
      </AddressWrapper>
    </Section>
  </MainContainer>
);

const Masterdata = () => {
  const { isLoading, fetchBusinessPartnerMetadata } = useBpStore();
  const { activeBpNumber } = useContextStore();
  const { t } = useI18n();

  const [masterData, setMasterData] = useState({});
  const toDisplayedMasterData = convertIntoGroupMasterData(masterData);
  const sectionLabels = {
    general: t(TranslationKeys.masterdata_section_general_label),
    address: t(TranslationKeys.masterdata_section_address_label),
    communication: t(TranslationKeys.masterdata_section_communication_label),
    bankAndVat: t(TranslationKeys.masterdata_section_bankAndVat_label),
    transport: t(TranslationKeys.masterdata_section_transport_label),
  };

  const fieldLabels = {
    // Group 1: "General"
    title: t(TranslationKeys.masterdata_headline_title_label),
    name: t(TranslationKeys.masterdata_headline_name_label),
    id: t(TranslationKeys.masterdata_headline_bpNumber_label),
    office: t(TranslationKeys.masterdata_headline_office_label),
    euFarmNumber: t(TranslationKeys.masterdata_headline_euFarmNumber_label),
    personalIdentityCard: t(
      TranslationKeys.masterdata_headline_personalIdentityCardNumber_label
    ),
    personalIdentityCardValidFrom: t(
      TranslationKeys.masterdata_headline_personalIdentityCardValidFrom_label
    ),
    socialSecurityNumber: t(
      TranslationKeys.masterdata_headline_socialSecurityNumber_label
    ),

    // Group 2: "Address"
    streetName: t(TranslationKeys.masterdata_headline_street_label),
    houseNumber: t(TranslationKeys.masterdata_headline_houseNo_label),
    street_5: t(TranslationKeys.masterdata_headline_street5_label),
    postalCode: t(TranslationKeys.masterdata_headline_postalCode_label),
    city: t(TranslationKeys.masterdata_headline_city_label),
    district: t(TranslationKeys.masterdata_headline_district_label),

    // Group 3: "Communication"
    telephoneNumbers: t(TranslationKeys.masterdata_headline_telephone_label),
    fax: t(TranslationKeys.masterdata_headline_fax_label),
    emails: t(TranslationKeys.masterdata_headline_mail_label),

    // Group 4: "Bank & VAT"
    bankName: t(TranslationKeys.masterdata_headline_bankName_label),
    bankAccountNumber: t(
      TranslationKeys.masterdata_headline_bankAccountNr_label
    ),
    bankCode: t(TranslationKeys.masterdata_headline_bankCode_label),
    iban: t(TranslationKeys.masterdata_headline_iban_label),
    taxNumber: t(TranslationKeys.masterdata_headline_taxNumber_label),
    vatNumber: t(TranslationKeys.masterdata_headline_vatNumber_label),
    vatOrRR: t(TranslationKeys.masterdata_headline_vatOrRR_label),

    // Group 5: "Transport"
    bezungspunkt: t(TranslationKeys.masterdata_headline_bezugspunkt_label),
    bzpinKm: t(TranslationKeys.masterdata_headline_bzpInKm_label),
    frachtsatzUeberruebenFuerBZP: t(
      TranslationKeys.masterdata_headline_frachtsatzUeberrueben_label
    ),
    frachtsatzProdukteFuerBZP: t(
      TranslationKeys.masterdata_headline_frachtsatzProdukte_label
    ),
  };

  useEffect(() => {
    fetchBusinessPartnerMetadata();
  }, [fetchBusinessPartnerMetadata, activeBpNumber]);

  useEffect(() => {
    const fetchData = async () => {
      const response =
        await cropConnectApiClient.getCurrentBusinessPartnerExtendedMetadata();
      if (response.success) setMasterData(response.data);
    };
    fetchData();
  }, [activeBpNumber]);

  if (isLoading) {
    return <MasterdataSkeleton />;
  }

  return (
    <MainContainer>
      <PageHeader title={t(TranslationKeys.masterdata_pageHeader)} />
      <SectionContainer>
        {Object.entries(toDisplayedMasterData).map(([sectionKey, fields]) => {
          const sectionTitle =
            sectionLabels[sectionKey as keyof typeof sectionLabels] ||
            sectionKey;
          return (
            <Section key={sectionKey}>
              <SectionTitle>{sectionTitle}</SectionTitle>
              <DataRowContainer>
                {Object.entries(fields).map(([fieldKey, fieldValue]) => {
                  const label =
                    fieldLabels[fieldKey as keyof typeof fieldLabels] ||
                    fieldKey;
                  return (
                    <DataRow
                      key={fieldKey}
                      label={label}
                      value={fieldValue as string}
                      note={
                        fieldKey === 'frachtsatzProdukteFuerBZP'
                          ? (t(
                              TranslationKeys.masterdata_transport_explaination_note
                            ) as string)
                          : undefined
                      }
                      editable={false}
                      onChange={() => {}}
                    />
                  );
                })}
              </DataRowContainer>
            </Section>
          );
        })}
      </SectionContainer>
    </MainContainer>
  );
};

export default Masterdata;
